import * as React from 'react';
import { cn } from '../../lib/cn';

export type MessageRole = 'user' | 'assistant' | 'system';

export interface MessageBubbleProps {
  role?: MessageRole;
  children?: React.ReactNode;
  time?: React.ReactNode;
  /** Only the last bubble of a run keeps the speech-tail corner. */
  showTail?: boolean;
  avatar?: React.ReactNode;
  /** Show a blinking streaming cursor at the end. */
  streaming?: boolean;
  className?: string;
}

/**
 * A single chat message, shared by the web app (S2) and widget (S3).
 * Handles user/assistant/system roles, grouping tails, timestamp, avatar, streaming.
 */
export function MessageBubble({
  role = 'assistant',
  children,
  time,
  showTail = true,
  avatar,
  streaming = false,
  className,
}: MessageBubbleProps) {
  if (role === 'system') {
    return (
      <div className={cn('my-1 max-w-[90%] self-center text-center', className)}>
        <span className="rounded-full bg-muted px-[var(--space-2-5)] py-[var(--space-1)] text-xs text-fg-muted">
          {children}
        </span>
      </div>
    );
  }

  const isUser = role === 'user';
  const r = 'var(--bubble-radius)';
  const tail = 'var(--bubble-radius-tail)';
  const borderRadius = isUser
    ? `${r} ${r} ${showTail ? tail : r} ${r}`
    : `${r} ${r} ${r} ${showTail ? tail : r}`;

  return (
    <div className={cn('flex flex-col gap-0.5', isUser ? 'items-end' : 'items-start', className)}>
      <div
        className={cn(
          'flex w-full items-end gap-2',
          isUser ? 'flex-row-reverse' : 'flex-row',
        )}
      >
        {!isUser ? avatar : null}
        <div
          className={cn(
            'break-words px-[var(--bubble-px)] py-[var(--bubble-py)] text-base leading-snug',
            // Assistant: fixed width at the max (no shrinking to a tiny spinner/caret
            // bubble while streaming). User: hug content (short prompts, right-aligned).
            isUser
              ? 'max-w-[var(--bubble-max-width)] bg-bubble-user text-bubble-user-fg'
              : 'w-[var(--bubble-max-width)] max-w-full bg-bubble-bot text-bubble-bot-fg',
          )}
          style={{ borderRadius }}
        >
          {children}
          {streaming ? (
            <span
              aria-hidden="true"
              className="ml-0.5 inline-block h-[1em] w-0.5 animate-[lb-blink_1s_steps(2)_infinite] bg-current align-text-bottom"
            />
          ) : null}
        </div>
      </div>
      {time ? <span className="px-1 text-2xs text-fg-muted">{time}</span> : null}
    </div>
  );
}
